import { useCallback, useMemo } from 'react';
import { useControlledProp, UseControlledPropArgs } from '@tanium/react-use-controlled-prop';
import {
  SELECTION_MODE_EXCLUDE,
  SELECTION_MODE_INCLUDE,
  SELECTION_STATUS_ALL,
  SELECTION_STATUS_NONE,
  SELECTION_STATUS_SOME,
} from './constants';
import {
  IsSelectable,
  SelectionModeType,
  SelectionProps,
  SelectionState,
  UseSelectionProps,
} from './types';

/**
 * Helper function to get the count of selected items.
 */
export const getSelectedCount = <T>(
  selection: Set<T>,
  mode: SelectionModeType = SELECTION_MODE_INCLUDE,
  availableItems: number,
) => (mode === SELECTION_MODE_INCLUDE ? selection.size : availableItems - selection.size);

/**
 * Helper function to determine the overall selection status
 */
export const getSelectionStatus = <T>(
  selection: Set<T>,
  mode: SelectionModeType,
  availableItems: number,
) => {
  if (
    availableItems > 0 &&
    ((mode === SELECTION_MODE_EXCLUDE && selection.size === 0) ||
      (mode === SELECTION_MODE_INCLUDE && selection.size === availableItems))
  ) {
    return SELECTION_STATUS_ALL;
  }

  if (
    (mode === SELECTION_MODE_EXCLUDE && selection.size < availableItems) ||
    (mode === SELECTION_MODE_INCLUDE && selection.size < availableItems && selection.size > 0)
  ) {
    return SELECTION_STATUS_SOME;
  }

  return SELECTION_STATUS_NONE;
};

const getValidSelectionMode = (mode?: SelectionModeType): SelectionModeType => {
  if (mode === SELECTION_MODE_INCLUDE || mode === SELECTION_MODE_EXCLUDE) {
    return mode;
  }
  return SELECTION_MODE_INCLUDE;
};

/**
 * Helper function to identify if an item is selected
 */
export const isItemSelected = <T>(selection: Set<T>, mode: SelectionModeType, id: T) => {
  if (selection.has(id)) {
    return mode === SELECTION_MODE_INCLUDE;
  }
  return mode === SELECTION_MODE_EXCLUDE;
};

/**
 * Generic Hook for managing selection state in either a controlled or uncontrolled way. Selected
 * rows are stored as a set of ids, and can be accessed through the `onSelectionChange` event.
 * `selectionMode` is used to handle Select/Deselect cases and defaults to `INCLUDE` mode.
 *
 * The isSelectable prop can be a function, which is passed through for the consumer to use
 * to determine whether a given row is selectable. It is the responsibility of the consumer
 * to exclude non-selectable rows in selectAll or EXCLUDE scenarios.
 */
export const useSelection = <TId, TIsSelectable extends IsSelectable<TId>>({
  defaultSelection: defaultSelectionProp,
  defaultSelectionMode: defaultSelectionModeProp,
  selection: selectionProp,
  onSelectionChange,
  enableSelectAll,
  isSelectable,
  selectionMode: selectionModeProp,
}: UseSelectionProps<TId, TIsSelectable>): SelectionProps<TId, TIsSelectable> => {
  // If either of these props were provided, this is controlled.
  const controlledPropsExist = !!selectionProp || !!selectionModeProp;

  const emptySet = useMemo(() => new Set<TId>(), []);

  const uncontrolledSelectionState = useMemo(
    () =>
      controlledPropsExist
        ? undefined
        : {
            selection: defaultSelectionProp || emptySet,
            selectionMode: getValidSelectionMode(defaultSelectionModeProp),
          },
    [controlledPropsExist, defaultSelectionModeProp, defaultSelectionProp, emptySet],
  );

  const controlledSelectionState = useMemo(
    () =>
      controlledPropsExist
        ? {
            selection: selectionProp || emptySet,
            selectionMode: getValidSelectionMode(selectionModeProp),
          }
        : undefined,
    [controlledPropsExist, emptySet, selectionModeProp, selectionProp],
  );

  const useControlledPropArgs = useMemo<UseControlledPropArgs<SelectionState<TId>>>(
    () => ({
      defaultValue: uncontrolledSelectionState,
      componentName: 'useSelection',
      onUpdate: ({ value }) => {
        onSelectionChange?.(value);
      },
      propName: 'selectionState',
      value: controlledSelectionState,
    }),
    [controlledSelectionState, onSelectionChange, uncontrolledSelectionState],
  );

  const [selectionState, setSelectionState] =
    useControlledProp<SelectionState<TId>>(useControlledPropArgs);

  const toggleSelection = useCallback(
    (id: TId) => {
      setSelectionState((prevState) => {
        const updated = new Set<TId>(prevState.selection);
        if (updated.has(id)) {
          updated.delete(id);
        } else {
          updated.add(id);
        }
        return { selectionMode: prevState.selectionMode, selection: updated };
      });
    },
    [setSelectionState],
  );

  const toggleMode = useCallback(() => {
    setSelectionState((prevState) => {
      const newMode =
        prevState.selectionMode === SELECTION_MODE_INCLUDE
          ? SELECTION_MODE_EXCLUDE
          : SELECTION_MODE_INCLUDE;
      const updated = new Set<TId>();
      return { selectionMode: newMode, selection: updated };
    });
  }, [setSelectionState]);

  const clearSelection = useCallback(() => {
    setSelectionState({ selectionMode: SELECTION_MODE_INCLUDE, selection: new Set<TId>() });
  }, [setSelectionState]);

  const isIdSelected = useCallback(
    (id: TId) => {
      const { selection, selectionMode } = selectionState;
      return isItemSelected<TId>(selection, selectionMode, id);
    },
    [selectionState],
  );

  const selectAll = useCallback(() => {
    setSelectionState({ selectionMode: SELECTION_MODE_EXCLUDE, selection: new Set<TId>() });
  }, [setSelectionState]);

  return {
    selection: selectionState.selection,
    toggleSelection,
    enableSelectAll,
    isSelectable,
    isIdSelected,
    selectAll,
    selectionMode: selectionState.selectionMode,
    toggleMode,
    clearSelection,
  };
};
